import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  MoreHoriz as MoreHorizIcon,
} from "@mui/icons-material";
import type { PaletteMode } from "@mui/material";
import type { Session } from "./app/api";

function Header({
  search,
  onSearchChange,
  setShowProgressDialog,
  session,
  colorMode,
  onColorModeToggle,
  onLogin,
  onLogout,
}: {
  search: string;
  onSearchChange: (newSearch: string) => void;
  setShowProgressDialog: (show: boolean) => void;
  session: Session;
  colorMode: PaletteMode;
  onColorModeToggle: () => void;
  onLogin: (username: string, password: string) => Promise<void>;
  onLogout: () => void;
}) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("md"));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoginError("");
    setLoggingIn(true);

    try {
      await onLogin(username.trim(), password);
      setLoginOpen(false);
      setUsername("");
      setPassword("");
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <>
      <Toolbar disableGutters sx={{ padding: 1 }}>
        {desktop && (
          <Typography
            variant="h6"
            sx={{
              flex: "0 0 auto",
              fontWeight: 700,
              marginLeft: 1,
              marginRight: 2,
            }}
          >
            FlareDrive
          </Typography>
        )}
        <InputBase
          size="small"
          placeholder="Search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          sx={{
            backgroundColor: (theme) => theme.palette.action.hover,
            borderRadius: "999px",
            flexGrow: 1,
            maxWidth: { xs: "none", md: 640 },
            padding: "8px 16px",
          }}
        />
        <Box sx={{ flexGrow: 1, display: { xs: "none", md: "block" } }} />
        <Tooltip title={colorMode === "dark" ? "Light mode" : "Dark mode"}>
          <IconButton
            aria-label={
              colorMode === "dark" ? "Switch to light mode" : "Switch to dark mode"
            }
            color="inherit"
            sx={{ marginLeft: 0.5 }}
            onClick={onColorModeToggle}
          >
            {colorMode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>
        <IconButton
          aria-label="More"
          color="inherit"
          sx={{ marginLeft: 0.5 }}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <MoreHorizIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          {session.authenticated && (
            <MenuItem disabled>{session.account?.username}</MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setShowProgressDialog(true);
            }}
          >
            Progress
          </MenuItem>
          {session.authenticated ? (
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onLogout();
              }}
            >
              Sign out
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setLoginOpen(true);
              }}
            >
              Sign in
            </MenuItem>
          )}
        </Menu>
      </Toolbar>

      <Dialog open={loginOpen} onClose={() => setLoginOpen(false)} fullWidth>
        <Box component="form" onSubmit={handleLogin}>
          <DialogTitle>Sign in</DialogTitle>
          <DialogContent sx={{ display: "grid", gap: 2, paddingTop: 1 }}>
            {loginError && <Alert severity="error">{loginError}</Alert>}
            <TextField
              autoFocus
              label="Username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLoginOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loggingIn || !username.trim() || !password}
            >
              Sign in
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}

export default Header;
